import React, { useEffect, useState } from 'react';
import { TabItem } from 'selfly-ui-react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { withRouter } from 'react-router';
import { groupBy, forEach } from 'lodash';

import { fetchCabinets, fetchLocations } from '../../actions/cabinets';
import { Loader, LoaderError } from './../commons/utils';
import ReportsAutomation from './tabs/reportsAutomation';
import NotificationAutomation from './tabs/notifications';
import RefillPlanAutomation from './tabs/refillPlanAutomation';
import { fetchRefillRules } from '../../actions/controlPanel';

const Automations = ({ fetchCabinets, fetchLocations, cabinets, organization, fetchRefillRules, refillSchemas, t, history }) => {
    const [loading, setLoading] = useState(true);
    const [hasErrors, setHasErrors] = useState(false);
    const [cabinetsByLocations, setCabinetsByLocations] = useState({});
    const [activeTab, setActiveTab] = useState('transactions');
    const [currentOrganization] = useState(organization);

    //when component loads, fetch all necessary data
    useEffect(() => {
        const apiRequest = [];

        if (!cabinets.fetched) apiRequest.push(fetchCabinets());
        if (!cabinets.locationsFetched) apiRequest.push(fetchLocations());
        if (!refillSchemas.fetched && !refillSchemas.isFetching) apiRequest.push(fetchRefillRules());

        Promise.all(apiRequest)
            .then(() => setLoading(false))
            .catch(e => {
                setHasErrors(true);
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // if organization is changed, refech the cabinets and locations
    useEffect(() => {
        if (currentOrganization === organization) return;
        setLoading(true);
        Promise.all([fetchCabinets(), fetchLocations(), fetchRefillRules()])
            .then(() => setLoading(false))
            .catch(e => {
                setHasErrors(true);
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization]);

    // when cabinets load, arrarge then by location
    useEffect(() => {
        if (cabinets.isFetching) return;

        const allCabinets = cabinets.cabinets || [];
        const allLocations = cabinets.locations || [];
        const groupCabinets = groupBy(allCabinets, 'locationId');

        const locationNameMapping = {};

        // map cabinets to locationName.
        forEach(groupCabinets, (cabinets, locationId) => {
            if (locationId === 'null') {
                locationNameMapping[t('noLocation')] = cabinets;
            } else {
                const locationName = allLocations.find(location => location.id === locationId)?.name;
                if (locationName) {
                    locationNameMapping[locationName] = cabinets;
                }
            }
        });
        setCabinetsByLocations(locationNameMapping);
    }, [cabinets, t]);

    if (loading) {
        return <Loader />;
    }

    if (hasErrors) {
        return <LoaderError />;
    }

    return (
        <div className="container-fluid automations">
            <div className="automations__tabs">
                <TabItem isActive={activeTab === 'transactions'} onClick={() => setActiveTab('transactions')}>
                    {t('automations:transactionReports')}
                </TabItem>
                <TabItem isActive={activeTab === 'inventory'} onClick={() => setActiveTab('inventory')}>
                    {t('automations:inventoryReports')}
                </TabItem>
                <TabItem isActive={activeTab === 'refill'} onClick={() => setActiveTab('refill')}>
                    {t('automations:refillReports')}
                </TabItem>
                <TabItem isActive={activeTab === 'notifications'} onClick={() => setActiveTab('notifications')}>
                    {t('automations:notifications')}
                </TabItem>
                <TabItem isActive={activeTab === 'refillPlans'} onClick={() => setActiveTab('refillPlans')}>
                    {t('automations:refillPlan')}
                </TabItem>
            </div>
            {(() => {
                switch (activeTab) {
                    case 'transactions':
                        return (
                            <ReportsAutomation
                                bannerText="automations:transactionReportTitle"
                                reportType="transactionReport"
                                name="Transaction Report"
                                cabinetsByLocations={cabinetsByLocations}
                                cancelPage={() => history.goBack()}
                                t={t}
                            />
                        );

                    case 'inventory':
                        return (
                            <ReportsAutomation
                                bannerText="automations:inventoryReportTitle"
                                reportType="inventoryReport"
                                name="Inventory Report"
                                cabinetsByLocations={cabinetsByLocations}
                                cancelPage={() => history.goBack()}
                                t={t}
                            />
                        );

                    case 'refill':
                        return (
                            <ReportsAutomation
                                bannerText="automations:refillReportTitle"
                                reportType="refillReport"
                                name="Refill Report"
                                cabinetsByLocations={cabinetsByLocations}
                                cancelPage={() => history.goBack()}
                                t={t}
                            />
                        );

                    case 'notifications':
                        return <NotificationAutomation cabinetsByLocations={cabinetsByLocations} cancelPage={() => history.goBack()} t={t} />;

                    case 'refillPlans':
                        return (
                            <RefillPlanAutomation
                                cabinetsByLocations={cabinetsByLocations}
                                refillSchemas={refillSchemas.refillRules || []}
                                cancelPage={() => history.goBack()}
                                t={t}
                            />
                        );

                    default:
                        return <></>;
                }
            })()}
        </div>
    );
};

export default withRouter(
    connect(
        state => ({
            cabinets: state.cabinets,
            organization: state.user.group.Name,
            refillSchemas: state.refillRules,
        }),
        {
            fetchCabinets,
            fetchLocations,
            fetchRefillRules,
        }
    )(translate()(Automations))
);
